/**
 * Hooks React Query do perfil da própria clínica do owner/admin.
 *
 * RLS: `clinics_select_member` libera leitura quando user é membro
 * ativo (owner/admin/etc.). UPDATE só passa pra `clinic_owner` ou
 * `clinic_admin` (`clinics_update_admin` em migration 0003).
 *
 * **Não confundir** com `features/clinics/` (leitura cross-clinic do
 * superadmin, sem write) ou `features/plans-admin/` (catálogo de
 * planos do superadmin).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase.js';

import type { ClinicAddressInput, ClinicProfileInput } from './schemas.js';

export const CLINIC_PROFILE_KEYS = {
  detail: (clinicId: string) => ['clinic-profile', 'detail', clinicId] as const,
};

/**
 * Linha de `clinics` lida pra alimentar o form de Perfil. Inclui campos
 * read-only (legal_name, cnpj, status) que aparecem na UI como travados
 * + campos editáveis (trade_name, email, phone, address).
 *
 * `logo_url` e `theme_color` são lidos mas NÃO mostrados em PR-M2-01 —
 * ficam reservados pra PR-M2-04 (identidade visual com paletas curadas).
 */
export interface ClinicProfileRow {
  id: string;
  legal_name: string;
  trade_name: string | null;
  cnpj: string | null;
  email: string | null;
  phone: string | null;
  address: ClinicAddressInput;
  status: string;
  logo_url: string | null;
  theme_color: string | null;
}

export function useClinicProfile(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: CLINIC_PROFILE_KEYS.detail(clinicId ?? ''),
    enabled: Boolean(clinicId),
    staleTime: 15_000,
    queryFn: async (): Promise<ClinicProfileRow | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase
        .from('clinics')
        .select(
          'id, legal_name, trade_name, cnpj, email, phone, address, status, logo_url, theme_color',
        )
        .eq('id', clinicId)
        .is('deleted_at', null)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as ClinicProfileRow | null) ?? null;
    },
  });
}

/**
 * Atualiza dados editáveis do perfil. Aceita o output já normalizado
 * (`normalizeClinicProfileInput`) — caller decide quando aplicar.
 *
 * RLS bloqueia se user não é `clinic_owner`/`clinic_admin`.
 *
 * Após sucesso, invalida `CLINIC_PROFILE_KEYS.detail` da clínica.
 */
export function useUpdateClinicProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { clinicId: string; values: ClinicProfileInput }) => {
      const { clinicId, values } = input;
      // Schema Zod marca campos opcionais (`undefined`) — supabase types
      // com `exactOptionalPropertyTypes: true` rejeitam undefined; aceita
      // só `string | null`. Coercimos undefined → null antes do update.
      const { error } = await supabase
        .from('clinics')
        .update({
          trade_name: values.trade_name ?? null,
          email: values.email ?? null,
          phone: values.phone ?? null,
          // address é JSONB no banco — precisa cast pra Json type do supabase-js
          address: values.address as never,
        })
        .eq('id', clinicId)
        .is('deleted_at', null);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: CLINIC_PROFILE_KEYS.detail(variables.clinicId) });
    },
  });
}
